import { CircularProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useSegment } from 'common/hooks/segment';
import { PresetView, usePreset } from 'pages/Shipments/Containers/types';
import React from 'react';
import { getShippersListCount, WatchlistQueryKeys } from 'services/clients/watchList';
import { SegmentEvent } from 'services/helpers/segment';
import { formatNumber } from 'utils/common';

export const useTrackSegment = (presetView: PresetView, eventName: string) => {
  const { track } = useSegment();

  const preset = usePreset(presetView);

  const handleClick = React.useCallback(() => {
    track(
      SegmentEvent.ShipmentWatchlistPrefilteredViewUsed,
      {
        from: eventName,
        to: presetView,
        filters: preset.filters,
      },
      { includeUser: true }
    );
  }, [track, presetView, preset, eventName]);

  return handleClick;
};

export const useCountFromAPI = (
  presetView: PresetView
): {
  inner: React.ReactNode;
  preset: ReturnType<typeof usePreset>;
  handleClick: () => void;
} => {
  const preset = usePreset(presetView);

  const filters = React.useMemo(() => {
    return { onlyShipments: true, ...preset.filters };
  }, [preset.filters]);

  const handleClick = useTrackSegment(presetView, 'shipments-dashboard');

  const countQuery = useQuery({
    queryKey: [WatchlistQueryKeys.ShippersListCount, filters],
    queryFn: ({ signal }) => getShippersListCount({ filters, signal }),
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error(error);
    },
  });

  let inner: React.ReactNode = <CircularProgress size={20} className="mt-2" />;
  if (countQuery.data) {
    inner = formatNumber(countQuery.data.count);
  } else if (countQuery.error) {
    inner = <span>Unavailable</span>;
  }

  return {
    inner,
    preset,
    handleClick,
  };
};
